import { useEffect, useMemo, useState } from "react";
import { getFacilityListApi } from "../../../services/facilities.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID } from "../../../constants/defaultKeys";


export const useOrgFacilities = () => {
  const currentFacilityId = Number(getCookie(FacilityID)) || null;

  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  /* The list could not be read at all, which is not the same as reading it
     and finding nowhere to send stock. */
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    getFacilityListApi()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data ?? res?.data?.results ?? [];
        setFacilities(
          (Array.isArray(rows) ? rows : []).filter(
            (f) => f?.is_active !== false && f?.is_deleted !== true
          )
        );
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setFacilities([]);
          setLoaded(false);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const destinations = useMemo(
    () =>
      facilities
        .filter((f) => Number(f?.id) !== currentFacilityId)
        .map((f) => ({ value: f.id, label: f.name })),
    [facilities, currentFacilityId]
  );

  const currentFacilityName =
    facilities.find((f) => Number(f?.id) === currentFacilityId)?.name || "-";

  /*
   * Whether there is anywhere to transfer stock to - which is not known until
   * the facility list is back.
   *
   * This used to answer "yes" while that was still in flight, so on a single
   * facility org the Stock Transfer button painted itself onto the Medicines
   * head and then vanished a moment later. A control that appears and leaves
   * reads as a glitch, and one that arrives a beat late reads as nothing at
   * all, so the honest answer while the list is loading is no.
   *
   * A request that failed is a different answer again: nothing is known, and
   * hiding the way to a screen because a list could not be read strands
   * anyone who needed it. That case offers the button and lets the transfer
   * screen say what it finds.
   */
  const canTransfer = failed || (loaded && destinations.length > 0);

  return {
    facilities,
    destinations,
    currentFacilityId,
    currentFacilityName,
    loading,
    facilityCountKnown: loaded,
    canTransfer,
  };
};

export default useOrgFacilities;
